import { OrangeButton } from "@/components/Buttons"
import { generateQR } from "../api/generate_qr"
import { FaCopy, FaDownload, FaQrcode, FaTimes } from "react-icons/fa"
import { useState } from "react"
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { toast } from "sonner"

interface QRResponse {
    success: boolean
    qr_code: string
    download_url: string
    message?: string
}

export function GenerateQRButton({ project_id }: { project_id: string }) {
    const [qrCode, setQrCode] = useState<string | null>(null)
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    const handleOnClick = async () => {
        setIsLoading(true)
        try {
            console.log("Generating QR")
            const response: QRResponse = await generateQR(project_id)
            console.log(response)
            
            if (response.success && response.qr_code) {
                setQrCode(response.qr_code)
                setDownloadUrl(response.download_url)
            } else {
                console.error("Failed to generate QR code:", response.message)
                alert("Failed to generate QR code: " + (response.message || "Unknown error"))
            }
        } catch (error) {
            console.error("Error generating QR code:", error)
            alert("Error generating QR code")
        } finally {
            setIsLoading(false)
        }
    }

    const copyDownloadUrl = () => {
        if (!downloadUrl) return
        navigator.clipboard.writeText(downloadUrl)
        toast.success("Download URL copied to clipboard")
    }

    const downloadQRCode = () => {
        if (!qrCode) return
        
        const link = document.createElement('a')
        link.href = qrCode
        link.download = `project_${project_id}_qr_code.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <>
            <OrangeButton onClick={handleOnClick} disabled={isLoading}>
                <div className="flex flex-row gap-2 items-center">
                    <FaQrcode />
                    {isLoading ? "Generating..." : "Generate QR Code"}
                </div>
            </OrangeButton>
            
            <AlertDialog open={!!qrCode}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle>QR Code Generated</AlertDialogTitle>
                        <AlertDialogDescription>
                            Scan this QR code to download project documents
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    
                    <div className="flex flex-col items-center gap-4 py-4">
                        {qrCode && (
                            <img 
                                src={qrCode} 
                                alt="QR Code for project download" 
                                className="w-48 h-48 border border-gray-200"
                            />
                        )}
                        <p>Scan to Download</p>

                        <div className="flex gap-2 items-center bg-gray-100 p-4 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500">{downloadUrl}</p>
                            <OrangeButton onClick={copyDownloadUrl} className="flex-1">
                                <div className="flex flex-row gap-2 items-center justify-center">
                                    <FaCopy />
                                    Copy
                                </div>
                            </OrangeButton>
                        </div>
                    </div>
                    
                    <AlertDialogFooter className="flex gap-2">
                        <OrangeButton onClick={downloadQRCode} className="flex-1">
                            <div className="flex flex-row gap-2 items-center justify-center">
                                <FaDownload />
                                Download QR Code
                            </div>
                        </OrangeButton>
                        <OrangeButton onClick={() => setQrCode(null)}>
                            <div className="flex flex-row gap-2 items-center justify-center">
                                <FaTimes />
                                Close
                            </div>
                        </OrangeButton>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}